import { Environment } from '../../types/config';
import { environment, getUrlsForEnvironment } from '../../environment';
import { LOG_TYPE, MetricService } from './MetricService';

/**
 * Makes unauthenticated API calls
 */
export class ApiService {
  protected defaultHeaders: RequestInit = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    mode: 'cors',
  };
  protected environment: Environment;
  protected _name = 'Api';

  constructor(environment: Environment) {
    this.environment = environment;
  }

  get bombbombApiUrl(): string {
    const { apiUrl } = getUrlsForEnvironment(environment);
    return `${apiUrl}/v2`;
  }

  get bombbombUrl(): string {
    return `https://${this.environmentPath}app.bombbomb.com`;
  }

  get = (url: string | URL, requestHeaders?: RequestInit): Promise<Response | null> => {
    const getHeaders: RequestInit = {
      ...this.defaultHeaders,
      ...requestHeaders,
      headers: this.generateHeadersFromRequestAndDefault(requestHeaders),
      method: 'GET',
    };

    return this.send(url, getHeaders);
  };

  post = (url: string | URL, body: BodyInit, requestHeaders?: RequestInit): Promise<Response | null> => {
    const postHeaders: RequestInit = {
      ...this.defaultHeaders,
      ...requestHeaders,
      headers: this.generateHeadersFromRequestAndDefault(requestHeaders),
      method: 'POST',
      body,
    };

    return this.send(url, postHeaders);
  };

  delete = (url: string | URL, requestHeaders?: RequestInit): Promise<Response | null> => {
    const deleteHeaders: RequestInit = {
      ...this.defaultHeaders,
      ...requestHeaders,
      headers: this.generateHeadersFromRequestAndDefault(requestHeaders),
      method: 'DELETE',
    };

    return this.send(url, deleteHeaders);
  };

  patch = (url: string | URL, body: BodyInit, requestHeaders?: RequestInit): Promise<Response | null> => {
    const putHeaders: RequestInit = {
      ...this.defaultHeaders,
      ...requestHeaders,
      headers: this.generateHeadersFromRequestAndDefault(requestHeaders),
      method: 'PATCH',
      body,
    };

    return this.send(url, putHeaders);
  };

  protected get environmentPath(): string {
    if (this.environment === 'prod') {
      return '';
    } else {
      return 'dev.';
    }
  }

  protected generateHeadersFromRequestAndDefault(requestHeaders?: RequestInit) {
    let headers = {
      ...this.defaultHeaders.headers,
    };

    if (requestHeaders) {
      headers = {
        ...headers,
        ...requestHeaders.headers,
      };
    }

    return headers;
  }

  protected async send(url: string | URL, params: RequestInit): Promise<Response | null> {
    try {
      const urlString = url instanceof URL ? url.toString() : url;
      const response = await fetch(urlString, params);
      if (response.ok) {
        return response;
      }
      MetricService.log(`${this._name}.${params.method?.toLowerCase()}() call failed`, LOG_TYPE.ERROR, {
        url,
        status: response.status,
        statusText: response.statusText,
      });
    } catch (error) {
      MetricService.log(`${this._name}.${params.method?.toLowerCase()}() call failed`, LOG_TYPE.ERROR, {
        url,
        err: (error as any).toString(),
      });
    }

    MetricService.yardstick(`${this._name}.${params.method?.toLowerCase()}.failure`);
    return null;
  }
}

const apiServices = new Map<Environment, ApiService>();
export function getApiService(environment: Environment, reset = false) {
  if (reset) {
    apiServices.clear();
  }

  if (apiServices.has(environment)) {
    return apiServices.get(environment);
  }

  apiServices.set(environment, new ApiService(environment));
  return apiServices.get(environment);
}
